import React, { useState, useEffect } from "react";
import {
  FaChartLine,
  FaHandshake,
  FaCubes,
  FaBell,
  FaUsers,
  FaBookOpen,
  FaTimesCircle,
} from "react-icons/fa";
import { IoIosArrowDroprightCircle } from "react-icons/io";
import { Link } from "react-router-dom";
import { useMetrics } from "../contexts/MatricsContext";
import axios from "axios";
import AuthService from "../services/AuthService";
import { displayMessage } from "../Utils/helper";
import PageHeader from "../components/common/PageHeader";

const { REACT_APP_API_BASE_URL } = process.env;

const INSIGHT_VARIANTS = {
  review: { label: "Model Review" },
  collaboration: { label: "Collaboration" },
  consensus: { label: "Consensus Ready" },
};

function AnimatedIcon({ Icon }) {
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    setAnimate(true);
    const timer = setTimeout(() => setAnimate(false), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Icon className={`de-kpi-icon ${animate ? "de-icon-animate-onload" : ""}`} />
  );
}

function KpiInfoModal({ open, onClose, title, description, points }) {
  if (!open) return null;

  return (
    <div className="de-kpi-modal-overlay" onClick={onClose}>
      <div
        className="de-kpi-modal-card"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="de-kpi-modal-header">
          <h3 className="de-kpi-modal-title">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="de-kpi-modal-close"
            aria-label="Close"
          >
            <FaTimesCircle />
          </button>
        </div>

        <p className="de-kpi-modal-description">{description}</p>

        <div className="de-kpi-modal-why">
          <div className="de-kpi-modal-why-title">Why we use this</div>
          <ul className="de-kpi-modal-list">
            {points.map((item, index) => (
              <li key={index} className="de-kpi-modal-list-item">
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ Icon, title, value, onOpenInfo, infoText }) {
  return (
    <div
      className="de-kpi-card de-kpi-card-clickable"
      onClick={onOpenInfo}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onOpenInfo();
      }}
      aria-label={`${title}. ${infoText}`}
    >
      <div className="de-kpi-icon-wrap">
        <AnimatedIcon Icon={Icon} />
      </div>
      <div className="de-kpi-content">
        <h4 className="de-kpi-label">{title}</h4>
        <p className="de-kpi-value">{value}</p>
      </div>
    </div>
  );
}

function InsightHighlight({ children }) {
  return <span className="de-insight-highlight">{children}</span>;
}

function InsightCard({
  Icon,
  variant,
  title,
  description,
  actionLabel,
  actionLink,
  showBookIcon,
  onDelete,
  deleting,
}) {
  const theme = INSIGHT_VARIANTS[variant] || INSIGHT_VARIANTS.review;

  const cardClass = [
    "insight-card",
    `insight-variant-${variant}`,
    deleting ? "insight-card-deleting" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={`insight-feed-row insight-variant-${variant}`}>
      <span className="insight-feed-dot" aria-hidden="true" />
      <div className={cardClass}>
        <div className="insight-feed-top">
          <span className="insight-type-badge">{theme.label}</span>
          <button
            type="button"
            onClick={onDelete}
            disabled={deleting}
            className="insight-dismiss-btn"
            aria-label="Dismiss notification"
          >
            ×
          </button>
        </div>

        <div className="insight-feed-body insight-feed-body-stack">
          <div className="insight-icon-wrap">
            <Icon className="insight-feed-icon" />
          </div>

          <div className="insight-feed-content">
            <h4 className="insight-card-title">{title}</h4>
            <p className="insight-feed-text">{description}</p>
          </div>

          <Link to={actionLink} className="insight-feed-action insight-feed-action-stack">
            {actionLabel}
            {showBookIcon ? (
              <FaBookOpen size={12} />
            ) : (
              <IoIosArrowDroprightCircle size={14} />
            )}
          </Link>
        </div>
      </div>
    </div>
  );
}

function ActionCard({ title, text, btnText, btnLink, isAccent }) {
  return (
    <div className="de-action-card">
      <h3 className="de-action-card-heading">{title}</h3>
      <p className="de-action-card-text">{text}</p>
      <Link
        to={btnLink}
        className={`de-action-btn${isAccent ? " de-action-btn-accent" : ""}`}
        aria-label={btnText}
      >
        {btnText}
      </Link>
    </div>
  );
}

function mapNotificationToInsight(n) {
  const uploadedByName = n.uploaded_by || "Someone";
  const workflow_name = n.workflow_name || "New Workflow";
  const workflowId = n.workflow_id;

  if (n.task_name === "model_runs_to_review") {
    return {
      variant: "review",
      Icon: FaUsers,
      title: "Model run awaiting review",
      description: (
        <>
          <InsightHighlight>{workflow_name}</InsightHighlight> has finished processing
          and is ready for planner analysis.
        </>
      ),
      actionLabel: "Review Now",
      actionLink: `/workflow?workflow_id=${workflowId}`,
      showBookIcon: false,
    };
  }

  if (n.task_name === "consensus_plans_to_finalize") {
    if (n.reviewed_get_fp === false) {
      return {
        variant: "collaboration",
        Icon: FaHandshake,
        title: "New collaboration input",
        description: (
          <>
            <InsightHighlight>{uploadedByName}</InsightHighlight> added inputs on{" "}
            <InsightHighlight>{workflow_name}</InsightHighlight> — review their
            message to keep planning aligned.
          </>
        ),
        actionLabel: "Review Now",
        actionLink: `/wf/comparison?workflow_id=${workflowId}`,
        showBookIcon: false,
      };
    }

    return {
      variant: "consensus",
      Icon: FaChartLine,
      title: "Consensus plan ready",
      description: (
        <>
          <InsightHighlight>{workflow_name}</InsightHighlight> has all required sales
          inputs and is ready for your final approval.
        </>
      ),
      actionLabel: "Finalize Plan",
      actionLink: `/wf/comparison?workflow_id=${workflowId}`,
      showBookIcon: true,
    };
  }

  return null;
}

function Dashboard() {
  const { metrics, loading, fetchMetrics } = useMetrics();
  const [isExpanded, setIsExpanded] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [deletingIds, setDeletingIds] = useState(new Set());
  const [selectedKpi, setSelectedKpi] = useState(null);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  useEffect(() => {
    if (metrics?.notifications) {
      setNotifications(metrics.notifications.sort((a, b) => b.id - a.id));
    }
  }, [metrics]);

  const modelRunsToReview = notifications.filter(
    (n) => n.task_name === "model_runs_to_review"
  ).length;

  const consensusPlanReady = notifications.filter(
    (n) => n.task_name === "consensus_plans_to_finalize" && n.reviewed_get_fp !== false
  ).length;

  const collaborationInsight = notifications.filter(
    (n) => n.task_name === "consensus_plans_to_finalize" && n.reviewed_get_fp === false
  ).length;

  const pendingActionsTotal =
    modelRunsToReview + consensusPlanReady + collaborationInsight;

  const kpiInfoMap = {
    salesCollaboration: {
      title: "Sales Collaboration Index",
      description:
        "This metric shows how actively planners and sales teams are working together during the planning cycle.",
      points: [
        "It considers how often the planner shares data or plans with the sales team.",
        "It also considers how often sales responds with feedback or updates.",
        "A higher value means stronger collaboration and better alignment before finalizing the demand plan.",
      ],
    },
    activeForecastModels: {
      title: "Active Forecast Models",
      description:
        "This metric shows how many forecasting models are currently active and available in the tool, including ARIMA, SARIMA, ARIMAX, SARIMAX, XGBoost, and LightGBM.",
      points: [
        "It helps users understand the forecasting coverage available on the platform.",
        "It provides a quick overview of the active models available for planning, comparison, and execution.",
        "It is useful for tracking model adoption and operational readiness across both statistical and machine learning methods.",
      ],
    },
    pendingActions: {
      title: "Pending Actions",
      description:
        "This metric highlights all items that currently require user attention in the system. It provides a consolidated view of review tasks, planning updates, and collaboration-related activities so users can quickly identify what needs to be addressed next.",
      points: [
        `Model Runs to Review: ${modelRunsToReview} model run(s) are completed and waiting for validation, analysis, or approval before they can be finalized or used in planning.`,
        `Consensus Plans Ready: ${consensusPlanReady} consensus plan(s) are prepared and available for user review, confirmation, or further action.`,
        `Collaboration Insights: ${collaborationInsight} collaboration-related update(s), comment(s), or shared insight(s) are pending review and may require user follow-up.`,
        `Total Pending Actions: ${pendingActionsTotal} total item(s) currently need attention across model review, consensus planning, and collaboration workflows.`,
      ],
    },
  };

  const handleDismissNotification = async (id) => {
    setDeletingIds((prev) => new Set(prev).add(id));

    try {
      await axios.delete(`${REACT_APP_API_BASE_URL}/notifications/${id}`, {
        headers: {
          Authorization: AuthService.getAccessToken(),
          access_token: AuthService.getAccessToken(),
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });

      setTimeout(() => {
        setNotifications((prev) => prev.filter((n) => n.id !== id));
      }, 260);
    } catch (e) {
      console.error("Failed to dismiss notification", e);
      displayMessage("danger", "Failed to dismiss notification");
      setDeletingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  if (loading) {
    return (
      <div className="de-dash-loading">
        <div>Loading metrics...</div>
      </div>
    );
  }

  return (
    <div className="de-dash">
      <div className="de-dash-card">
        <div className="de-dash-inner">
          <PageHeader title="Welcome to Demand Edge" />

          <header className="de-dash-header">
            <h2 className="de-dash-header-title">KPI Scoreboard</h2>
          </header>

          <section className="de-dash-kpi-grid de-dash-kpi-grid-3">
            <KpiCard
              Icon={FaHandshake}
              title="Sales Collaboration Index"
              value={
                metrics?.sales_collab?.sales_collaboration_index?.collaboration_percentage
                  ? `${Math.min(
                      100,
                      metrics.sales_collab.sales_collaboration_index
                        .collaboration_percentage
                    )}%`
                  : "N/A"
              }
              onOpenInfo={() => setSelectedKpi("salesCollaboration")}
              infoText="Shows how actively planners and sales teams are collaborating."
            />
            <KpiCard
              Icon={FaCubes}
              title="Active Forecast Models"
              value="6"
              onOpenInfo={() => setSelectedKpi("activeForecastModels")}
              infoText="Shows how many forecast models are currently available in the tool."
            />
            <KpiCard
              Icon={FaBell}
              title="Pending Actions"
              value={pendingActionsTotal}
              onOpenInfo={() => setSelectedKpi("pendingActions")}
              infoText="Shows total items needing review or action."
            />
          </section>

          <section className="de-dash-action-grid">
            <ActionCard
              title="Craft New Forecast Scenarios"
              text="Leverage your data to predict future demand using the guided model builder."
              btnText="Start Building"
              btnLink="/CreateWorkflow"
            />
            <ActionCard
              title="Forecast Library"
              text="Translate your forecast into actionable inventory and supply plans."
              btnText="Go to Library"
              btnLink="/ManageWorkflow"
              isAccent
            />
            <ActionCard
              title="Sales Collaboration"
              text="Collaborate with Sales and other teams to align on a final demand plan."
              btnText="Start Planning"
              btnLink="/comparison"
            />
            <ActionCard
              title="Inventory Optimization"
              text="Translate your forecast into an actionable inventory and supply plan."
              btnText="Optimize Inventory"
              btnLink="/Indentation"
              isAccent
            />
            <ActionCard
              title="Supply Chain Management"
              text="Plan inventory replenishment and transport recommendations from your forecast output."
              btnText="Open SCM"
              btnLink="/SupplyChainManagement"
            />
            <ActionCard
              title="Trend Analysis & New Product Development"
              text="Explore forecast trends analysis and New Product Development insights in one place."
              btnText="Open Analysis"
              btnLink="/trend-conjoint"
            />
          </section>

          <div className="de-insight-section">
            <div className="de-insight-header">
              <div className="de-insight-header-left">
                <span className="de-insight-title">Proactive Insight Feed</span>
                {notifications?.length > 0 && (
                  <span className="de-insight-count">{notifications.length}</span>
                )}
              </div>
              {notifications?.length > 3 && (
                <button
                  type="button"
                  onClick={() => setIsExpanded(!isExpanded)}
                  className={`de-expand-btn${isExpanded ? " de-expand-btn-active" : ""}`}
                >
                  {isExpanded
                    ? "See Less"
                    : `See More (${notifications.length - 3})`}
                </button>
              )}
            </div>

            {!notifications || notifications.length === 0 ? (
              <div className="de-empty-insight">
                <div className="de-empty-insight-icon">!</div>
                <div>
                  <div className="de-empty-insight-title">
                    No proactive insights yet
                  </div>
                  <div className="de-empty-insight-text">
                    As new forecasts run and collaboration events happen, important
                    alerts and recommendations will appear here automatically.
                  </div>
                </div>
              </div>
            ) : (
              <div className="de-insight-list insight-feed-timeline">
                {notifications
                  .slice(0, isExpanded ? notifications.length : 3)
                  .map((n) => {
                    const insight = mapNotificationToInsight(n);
                    if (!insight) return null;

                    return (
                      <InsightCard
                        key={n.id}
                        variant={insight.variant}
                        Icon={insight.Icon}
                        title={insight.title}
                        description={insight.description}
                        actionLabel={insight.actionLabel}
                        actionLink={insight.actionLink}
                        showBookIcon={insight.showBookIcon}
                        deleting={deletingIds.has(n.id)}
                        onDelete={() => handleDismissNotification(n.id)}
                      />
                    );
                  })}
              </div>
            )}
          </div>
        </div>
      </div>

      <KpiInfoModal
        open={!!selectedKpi}
        onClose={() => setSelectedKpi(null)}
        title={selectedKpi ? kpiInfoMap[selectedKpi].title : ""}
        description={selectedKpi ? kpiInfoMap[selectedKpi].description : ""}
        points={selectedKpi ? kpiInfoMap[selectedKpi].points : []}
      />

      <style>{`
    .de-dash {
      width: 100%;
      margin: 0;
      padding: 0;
      box-sizing: border-box;
      background: #f4f3ef;
    }

    .de-dash-card {
      width: 100%;
      background: transparent;
      padding: 8px 4px 20px;
      border: none;
      overflow: visible;
    }

    .de-dash-inner {
      width: 100%;
      margin: 0 auto;
      color: #0c3c54;
      display: flex;
      flex-direction: column;
    }

    .de-dash-loading {
      display: flex;
      height: 60vh;
      align-items: center;
      justify-content: center;
      font-family: Poppins, sans-serif;
      color: #0c3c54;
      background: #f4f3ef;
    }

    .de-dash-header { margin-bottom: 14px; }

    .de-dash-header-title {
      font-size: 18px;
      font-weight: 700;
      color: #0c3c54;
      margin: 0;
      font-family: Poppins, sans-serif;
    }

    .de-dash-kpi-grid {
      display: grid;
      grid-template-columns: repeat(4, minmax(0, 1fr));
      gap: 14px;
      width: 100%;
    }

    .de-dash-kpi-grid-3 {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .de-kpi-card {
      background: linear-gradient(135deg, #021a30 0%, #032B4E 55%, #032B4E 100%);
      border-radius: 12px;
      padding: 16px 18px;
      transition: transform 0.2s ease, box-shadow 0.2s ease;
      color: #ffffff;
      box-shadow: 0 4px 14px rgba(3, 43, 78, 0.14);
      display: flex;
      align-items: center;
      gap: 14px;
      min-height: 96px;
      box-sizing: border-box;
      cursor: default;
    }

    .de-kpi-card-clickable {
      cursor: pointer;
    }

    .de-kpi-card:hover {
      transform: translateY(-3px);
      box-shadow: 0 10px 24px rgba(3, 43, 78, 0.22);
    }

    .de-kpi-icon-wrap {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .de-kpi-icon {
      font-size: 44px;
      color: #E06639;
      transition: transform 0.2s ease;
      cursor: default;
    }

    .de-kpi-content { min-width: 0; flex: 1; }

    .de-kpi-label {
      font-size: 13px;
      font-weight: 600;
      color: rgba(255, 255, 255, 0.9);
      margin: 0 0 4px;
      line-height: 1.3;
      font-family: Poppins, sans-serif;
    }

    .de-kpi-value {
      font-size: 22px;
      font-weight: 700;
      color: #ffffff;
      margin: 0;
      line-height: 1.1;
      font-family: Poppins, sans-serif;
    }

    .de-dash-action-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 14px;
      margin-top: 28px;
      margin-bottom: 28px;
    }

    .de-action-card {
      background: #fff;
      padding: 18px 20px;
      border-radius: 12px;
      border: 1px solid #e6eef4;
      box-shadow: 0 3px 12px rgba(12, 60, 84, 0.06);
      text-align: left;
      transition: transform 0.2s ease, box-shadow 0.2s ease;
      cursor: default;
    }

    .de-action-card:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(12, 60, 84, 0.1);
    }

    .de-action-card-heading {
      font-size: 16px;
      font-weight: 700;
      margin: 0 0 6px;
      color: #0c3c54;
      font-family: Poppins, sans-serif;
    }

    .de-action-card-text {
      font-size: 13px;
      font-weight: 400;
      color: #6a7d8c;
      margin: 0 0 14px;
      line-height: 1.5;
      font-family: Poppins, sans-serif;
    }

    .de-action-btn {
      display: inline-block;
      padding: 8px 14px;
      border-radius: 8px;
      text-decoration: none;
      color: #fff;
      font-weight: 600;
      font-size: 13px;
      transition: transform 0.12s ease, box-shadow 0.12s ease;
      box-shadow: 0 3px 10px rgba(224, 102, 57, 0.25);
      background: #E06639;
      font-family: Poppins, sans-serif;
    }

    .de-action-btn:hover {
      transform: translateY(-1px);
      box-shadow: 0 5px 14px rgba(224, 102, 57, 0.35);
    }

    .de-insight-section {
      margin-top: 4px;
      padding: 0;
      background: transparent;
      border: none;
      box-shadow: none;
    }

    .de-insight-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 14px;
      gap: 12px;
      flex-wrap: wrap;
    }

    .de-insight-header-left {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .de-insight-title {
      font-size: 16px;
      font-weight: 700;
      color: #0c3c54;
      font-family: Poppins, sans-serif;
    }

    .de-insight-count {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 22px;
      height: 22px;
      padding: 0 7px;
      border-radius: 999px;
      background: rgba(224, 102, 57, 0.12);
      color: #c64b2d;
      font-size: 11px;
      font-weight: 700;
      font-family: Poppins, sans-serif;
    }

    .de-insight-list {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .de-expand-btn {
      padding: 8px 14px;
      background: #E06639;
      color: #fff;
      border: none;
      border-radius: 8px;
      font-size: 12px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;
      box-shadow: 0 3px 10px rgba(224, 102, 57, 0.25);
      font-family: Poppins, sans-serif;
    }

    .de-expand-btn-active { background: #c64b2d; }

    .de-empty-insight {
      padding: 14px 16px;
      border-radius: 10px;
      border: 1px dashed #d4e3ed;
      background: #f6fafd;
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .de-empty-insight-icon {
      width: 36px;
      height: 36px;
      border-radius: 999px;
      background: #032B4E;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #E06639;
      font-weight: 700;
      font-size: 16px;
      flex-shrink: 0;
    }

    .de-empty-insight-title {
      font-weight: 600;
      font-size: 13px;
      color: #0c3c54;
      margin-bottom: 2px;
      font-family: Poppins, sans-serif;
    }

    .de-empty-insight-text {
      font-size: 12px;
      color: #6a7d8c;
      line-height: 1.45;
      font-family: Poppins, sans-serif;
    }

    .de-insight-highlight {
      color: #032B4E;
      font-weight: 700;
    }

    /* KPI info modal — TARGET logic, SOURCE visual language */
    .de-kpi-modal-overlay {
      position: fixed;
      inset: 0;
      background: rgba(3, 43, 78, 0.55);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 9999;
      padding: 20px;
    }

    .de-kpi-modal-card {
      width: 100%;
      max-width: 640px;
      background: #fff;
      border-radius: 12px;
      padding: 22px 22px 18px;
      box-shadow: 0 16px 48px rgba(3, 43, 78, 0.22);
      border: 1px solid #e6eef4;
      font-family: Poppins, sans-serif;
    }

    .de-kpi-modal-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;
      gap: 12px;
    }

    .de-kpi-modal-title {
      margin: 0;
      font-size: 18px;
      font-weight: 700;
      color: #0c3c54;
      font-family: Poppins, sans-serif;
    }

    .de-kpi-modal-close {
      border: none;
      background: transparent;
      cursor: pointer;
      color: #6a7d8c;
      font-size: 20px;
      line-height: 1;
      padding: 2px;
      border-radius: 6px;
      transition: color 0.15s ease, background 0.15s ease;
    }

    .de-kpi-modal-close:hover {
      color: #E06639;
      background: rgba(224, 102, 57, 0.08);
    }

    .de-kpi-modal-description {
      font-size: 13px;
      line-height: 1.6;
      color: #6a7d8c;
      margin: 0 0 14px;
      font-family: Poppins, sans-serif;
    }

    .de-kpi-modal-why {
      background: #f6fafd;
      border: 1px solid #e6eef4;
      border-radius: 10px;
      padding: 14px 16px;
    }

    .de-kpi-modal-why-title {
      font-size: 13px;
      font-weight: 700;
      margin-bottom: 8px;
      color: #032B4E;
      font-family: Poppins, sans-serif;
    }

    .de-kpi-modal-list {
      margin: 0;
      padding-left: 18px;
    }

    .de-kpi-modal-list-item {
      font-size: 13px;
      color: #6a7d8c;
      line-height: 1.55;
      margin-bottom: 6px;
      font-family: Poppins, sans-serif;
    }

    /* Insight feed timeline */
    .insight-feed-timeline {
      position: relative;
      padding-left: 4px;
    }

    .insight-feed-timeline::before {
      content: '';
      position: absolute;
      left: 11px;
      top: 18px;
      bottom: 18px;
      width: 2px;
      background: linear-gradient(180deg, #032B4E 0%, #e6eef4 100%);
      border-radius: 2px;
      opacity: 0.35;
    }

    .insight-feed-row {
      display: flex;
      align-items: stretch;
      gap: 12px;
      position: relative;
    }

    .insight-feed-dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      flex-shrink: 0;
      margin-top: 22px;
      z-index: 1;
    }

    .insight-card {
      flex: 1;
      position: relative;
      border-radius: 12px;
      padding: 10px 12px 12px;
      border: 1px solid #e6eef4;
      box-shadow: 0 2px 10px rgba(12, 60, 84, 0.05);
      cursor: default;
      min-width: 0;
      transition: transform 0.2s ease, opacity 0.2s ease, margin 0.2s ease, max-height 0.25s ease, box-shadow 0.2s ease, border-color 0.2s ease;
      max-height: 140px;
      overflow: hidden;
    }

    .insight-card:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 18px rgba(12, 60, 84, 0.1);
      border-color: #d4e3ed;
    }

    .insight-card-deleting {
      transform: translateX(-20px);
      opacity: 0;
      max-height: 0;
      margin-top: 0;
      margin-bottom: 0;
    }

    .insight-feed-top {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 8px;
      gap: 8px;
    }

    .insight-type-badge {
      display: inline-flex;
      align-items: center;
      padding: 3px 8px;
      border-radius: 999px;
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      font-family: Poppins, sans-serif;
    }

    .insight-dismiss-btn {
      border: none;
      background: transparent;
      color: #9ca3af;
      cursor: pointer;
      font-size: 18px;
      font-weight: 700;
      line-height: 1;
      padding: 2px 4px;
      border-radius: 6px;
      transition: color 0.15s ease, background 0.15s ease;
    }

    .insight-dismiss-btn:hover {
      color: #6b7280;
      background: rgba(12, 60, 84, 0.06);
    }

    .insight-feed-body {
      display: flex;
      align-items: flex-start;
      gap: 12px;
    }

    .insight-icon-wrap {
      width: 40px;
      height: 40px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .insight-feed-icon {
      font-size: 18px;
      flex-shrink: 0;
    }

    .insight-feed-content { flex: 1; min-width: 0; }

    .insight-card-title {
      margin: 0 0 3px;
      font-size: 13px;
      font-weight: 700;
      color: #0c3c54;
      line-height: 1.3;
      font-family: Poppins, sans-serif;
    }

    .insight-feed-text {
      margin: 0;
      font-size: 12px;
      color: #6a7d8c;
      font-weight: 400;
      line-height: 1.45;
      font-family: Poppins, sans-serif;
    }

    .insight-feed-action {
      color: #ffffff;
      font-weight: 600;
      font-size: 11px;
      transition: transform 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease;
      display: inline-flex;
      align-items: center;
      gap: 5px;
      border-radius: 8px;
      padding: 7px 11px;
      cursor: pointer;
      text-decoration: none;
      font-family: Poppins, sans-serif;
      white-space: nowrap;
      flex-shrink: 0;
      align-self: center;
    }

    .insight-feed-action-stack:hover {
      transform: translateY(-1px);
      opacity: 0.95;
    }

    /* Insight variants */
    .insight-variant-review .insight-feed-dot {
      background: #032B4E;
      box-shadow: 0 0 0 3px rgba(3, 43, 78, 0.1);
    }

    .insight-variant-review.insight-card {
      background: linear-gradient(135deg, #ffffff 0%, #f6fafd 100%);
    }

    .insight-variant-review .insight-type-badge {
      background: rgba(3, 43, 78, 0.1);
      color: #032B4E;
    }

    .insight-variant-review .insight-icon-wrap {
      background: linear-gradient(135deg, #e8f1f8 0%, #d4e6f2 100%);
    }

    .insight-variant-review .insight-feed-icon { color: #032B4E; }

    .insight-variant-review .insight-feed-action {
      background: #032B4E;
      box-shadow: 0 2px 8px rgba(3, 43, 78, 0.22);
    }

    .insight-variant-collaboration .insight-feed-dot {
      background: #E06639;
      box-shadow: 0 0 0 3px rgba(224, 102, 57, 0.12);
    }

    .insight-variant-collaboration.insight-card {
      background: linear-gradient(135deg, #ffffff 0%, #fffaf7 100%);
    }

    .insight-variant-collaboration .insight-type-badge {
      background: rgba(224, 102, 57, 0.12);
      color: #c64b2d;
    }

    .insight-variant-collaboration .insight-icon-wrap {
      background: linear-gradient(135deg, #fff4ee 0%, #fde8dc 100%);
    }

    .insight-variant-collaboration .insight-feed-icon { color: #E06639; }

    .insight-variant-collaboration .insight-feed-action {
      background: #E06639;
      box-shadow: 0 2px 8px rgba(224, 102, 57, 0.28);
    }

    .insight-variant-consensus .insight-feed-dot {
      background: #1e8e5a;
      box-shadow: 0 0 0 3px rgba(30, 142, 90, 0.12);
    }

    .insight-variant-consensus.insight-card {
      background: linear-gradient(135deg, #ffffff 0%, #f4fbf7 100%);
    }

    .insight-variant-consensus .insight-type-badge {
      background: rgba(30, 142, 90, 0.12);
      color: #1e8e5a;
    }

    .insight-variant-consensus .insight-icon-wrap {
      background: linear-gradient(135deg, #e8f7ef 0%, #d4f0e2 100%);
    }

    .insight-variant-consensus .insight-feed-icon { color: #1e8e5a; }

    .insight-variant-consensus .insight-feed-action {
      background: #1e8e5a;
      box-shadow: 0 2px 8px rgba(30, 142, 90, 0.25);
    }

    /* Animations */
    @keyframes de-swing {
      0%   { transform: rotate(0deg); }
      20%  { transform: rotate(-10deg); }
      40%  { transform: rotate(8deg); }
      60%  { transform: rotate(-6deg); }
      80%  { transform: rotate(4deg); }
      100% { transform: rotate(0deg); }
    }

    .de-icon-animate-onload {
      animation: de-swing 0.5s cubic-bezier(.36,.07,.19,.97) forwards;
    }

    /* Responsive */
    @media (max-width: 991px) {
      .de-dash-action-grid {
        grid-template-columns: 1fr;
      }
      .de-dash-kpi-grid,
      .de-dash-kpi-grid-3 {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }
    }

    @media (max-width: 767px) {
      .de-dash-kpi-grid,
      .de-dash-kpi-grid-3 {
        grid-template-columns: 1fr;
      }
      .insight-feed-body-stack {
        flex-direction: column;
        align-items: stretch;
      }
      .insight-feed-action-stack {
        width: 100%;
        justify-content: center;
      }
    }
      `}</style>
    </div>
  );
}

export default Dashboard;
